const UPLOAD_DATA_KEY = 'kunstpfote_uploads';
const SESSION_KEY = 'kunstpfote_session';
const MAX_FILES = 4;
const MAX_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// ─── Photo Management Panel ────────────────────────────────────────────────────

class PhotoManagementPanel extends HTMLElement {
  constructor() {
    super();
    this._uploadQueue = Promise.resolve();
  }

  connectedCallback() {
    // Re-render whenever the parent dialog opens
    const dialogEl = this.closest('dialog-component')?.querySelector('dialog[ref="dialog"]');
    if (dialogEl) {
      new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.attributeName === 'open' && dialogEl.hasAttribute('open')) {
            this.render();
          }
        }
      }).observe(dialogEl, { attributes: true, attributeFilter: ['open'] });
    }

    // Sync when other components change uploads or generate
    window.addEventListener('kunstpfote:photo-removed', () => this.render());
    window.addEventListener('kunstpfote:photos-uploaded', () => this.render());
    window.addEventListener('kunstpfote:generation-complete', () => this.render());

    // Lightbox
    const lightbox = document.getElementById('photo-mgmt-lightbox');
    if (lightbox) {
      lightbox.addEventListener('click', (e) => { if (e.target === lightbox) lightbox.close(); });
      lightbox.querySelector('.photo-mgmt__lightbox-close')
        ?.addEventListener('click', () => lightbox.close());
    }

    this.render();
  }

  // ─── Storage helpers ──────────────────────────────────────────────────────

  readData() {
    try {
      const raw = localStorage.getItem(UPLOAD_DATA_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw);
      if (!d.sessionId) return null;
      // Normalise generated: stored as { sku, url } objects (legacy: plain strings).
      const generated = (d.generated ?? [])
        .map((e) => {
          if (typeof e === 'string') {
            const sku = e.replace(/\.jpg$/i, '');
            return (sku && sku !== 'undefined') ? { sku, url: null } : null;
          }
          return (e && typeof e.sku === 'string' && e.sku !== 'undefined')
            ? { sku: e.sku, url: e.url || null }
            : null;
        })
        .filter(Boolean);

      return {
        sessionId: d.sessionId,
        originals: d.originals ?? [],
        generated,
        uploadBase: d.uploadBase ?? '',
      };
    } catch {
      return null;
    }
  }

  writeData(patch) {
    const existing = this.readData() || { originals: [], generated: [] };
    const updated = { ...existing, ...patch };
    localStorage.setItem(UPLOAD_DATA_KEY, JSON.stringify(updated));
    return updated;
  }

  getEndpoint() {
    const data = this.readData();
    if (data?.uploadBase) return `${data.uploadBase}/upload`;
    return this.dataset.uploadEndpoint || '';
  }

  buildUrl(sessionId, type, filename) {
    const data = this.readData();
    const base = data?.uploadBase || this.dataset.uploadEndpoint?.replace(/\/upload$/, '') || '';
    return `${base}/image/${sessionId}/${type}/${filename}`;
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  render() {
    const data = this.readData();
    const originals = data?.originals ?? [];
    const generated = data?.generated ?? [];
    const sessionId = data?.sessionId;
    const remaining = MAX_FILES - originals.length;

    let html = '';

    // Generated section — shown first
    if (generated.length > 0) {
      html += `<section class="photo-mgmt__section">
        <header class="photo-mgmt__section-head">
          <h3 class="photo-mgmt__section-title">Generierte Kunstporträts</h3>
          <span class="photo-mgmt__badge">${generated.length}</span>
        </header>
        <ul class="photo-mgmt__grid photo-mgmt__grid--generated" role="list">`;
      for (const { sku, url } of generated) {
        const src = this.buildUrl(sessionId, 'generated', `${sku}.jpg`);
        const imgTag = `<img src="${src}" alt="Generiertes Kunstporträt" loading="lazy" class="photo-mgmt__thumb">`;
        const inner = url
          ? `<a href="${url}" class="photo-mgmt__generated-link" title="Zum Produkt">${imgTag}</a>`
          : imgTag;
        html += `<li class="photo-mgmt__item photo-mgmt__item--generated">
          ${inner}
          <button class="photo-mgmt__del-generated" data-sku="${sku}" type="button" aria-label="Bild löschen" title="Bild löschen">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <button class="photo-mgmt__zoom" data-src="${src}" type="button" aria-label="Bild vergrößern" title="Bild vergrößern">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>
        </li>`;
      }
      html += `</ul></section>`;
    }

    // Originals section
    html += `<section class="photo-mgmt__section">
      <header class="photo-mgmt__section-head">
        <h3 class="photo-mgmt__section-title">Hochgeladene Bilder</h3>
        <span class="photo-mgmt__badge">${originals.length}&thinsp;/&thinsp;${MAX_FILES}</span>
      </header>`;

    if (originals.length > 0) {
      html += `<ul class="photo-mgmt__grid" role="list">`;
      for (const f of originals) {
        const src = this.buildUrl(sessionId, 'originals', f);
        html += `<li class="photo-mgmt__item">
          <img src="${src}" data-file="${f}" alt="" loading="lazy" class="photo-mgmt__thumb">
          <button class="photo-mgmt__del" data-file="${f}" type="button" aria-label="Bild entfernen" title="Bild entfernen">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </li>`;
      }
      html += `</ul>`;
    } else {
      html += `<p class="photo-mgmt__empty">Noch keine Fotos hochgeladen.<br><small>Füge auf der Produktseite dein Haustierfoto hinzu.</small></p>`;
    }

    if (remaining > 0) {
      const label = remaining === MAX_FILES
        ? 'Fotos hochladen'
        : `Weitere hochladen (noch ${remaining})`;
      html += `<label class="photo-mgmt__upload-label" tabindex="0" role="button"
          onkeydown="if(event.key==='Enter'||event.key===' ')this.querySelector('input').click()">
        <input type="file" class="photo-mgmt__file-input" accept="image/jpeg,image/png,image/webp" multiple tabindex="-1" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        ${label}
      </label>`;
    }

    html += `</section>`;

    html += `<div class="photo-mgmt__messages" aria-live="polite" role="status"></div>`;

    this.innerHTML = html;
    this.bindEvents();
    this.bindImageErrors();
  }

  bindEvents() {
    this.querySelectorAll('.photo-mgmt__del').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removePhoto(btn.dataset.file);
      });
    });

    this.querySelectorAll('.photo-mgmt__del-generated').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeGenerated(btn.dataset.sku);
      });
    });

    this.querySelectorAll('.photo-mgmt__zoom').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const lightbox = document.getElementById('photo-mgmt-lightbox');
        if (!lightbox) return;
        lightbox.querySelector('.photo-mgmt__lightbox-img').src = btn.dataset.src;
        lightbox.showModal();
      });
    });

    const input = this.querySelector('.photo-mgmt__file-input');
    if (input) {
      input.addEventListener('change', (e) => {
        this.handleFiles(e.target.files);
        input.value = '';
      });
    }
  }

  // ─── Broken image handling ────────────────────────────────────────────────

  bindImageErrors() {
    this.querySelectorAll('.photo-mgmt__thumb[data-file]').forEach((img) => {
      img.addEventListener('error', () => this.purgeBrokenEntry(img.dataset.file), { once: true });
    });
  }

  purgeBrokenEntry(filename) {
    const data = this.readData();
    if (!data) return;
    data.originals = data.originals.filter((f) => f !== filename);
    localStorage.setItem(UPLOAD_DATA_KEY, JSON.stringify(data));
    // Silently re-render – no server DELETE, image is already gone
    this.render();
  }

  // ─── Remove ───────────────────────────────────────────────────────────────

  async removePhoto(filename) {
    const data = this.readData();
    if (!data) return;

    const btn = Array.from(this.querySelectorAll('.photo-mgmt__del'))
      .find((b) => b.dataset.file === filename);
    if (btn) btn.disabled = true;

    const base = data.uploadBase || this.dataset.uploadEndpoint?.replace(/\/upload$/, '') || '';
    try {
      const res = await fetch(`${base}/image/${data.sessionId}/originals/${filename}`, {
        method: 'DELETE',
      });
      // 404 = already gone, treat as success
      if (!res.ok && res.status !== 404) {
        if (btn) btn.disabled = false;
        this.showMessage(
          res.status === 429
            ? 'Zu viele Anfragen – bitte kurz warten.'
            : `Löschen fehlgeschlagen (${res.status}).`,
          'error'
        );
        return;
      }
    } catch {
      if (btn) btn.disabled = false;
      this.showMessage('Löschen fehlgeschlagen.', 'error');
      return;
    }

    data.originals = data.originals.filter((f) => f !== filename);
    localStorage.setItem(UPLOAD_DATA_KEY, JSON.stringify(data));
    // Notify PDP upload block so it can sync its UI
    window.dispatchEvent(new CustomEvent('kunstpfote:photo-removed', { detail: { filename } }));
    this.render();
  }

  // ─── Remove generated ────────────────────────────────────────────────────

  async removeGenerated(sku) {
    const data = this.readData();
    if (!data) return;

    const btn = Array.from(this.querySelectorAll('.photo-mgmt__del-generated'))
      .find((b) => b.dataset.sku === sku);
    if (btn) btn.disabled = true;

    const base = data.uploadBase || this.dataset.uploadEndpoint?.replace(/\/upload$/, '') || '';
    try {
      const res = await fetch(`${base}/image/${data.sessionId}/generated/${sku}`, {
        method: 'DELETE',
      });
      if (!res.ok && res.status !== 404) {
        if (btn) btn.disabled = false;
        this.showMessage(
          res.status === 429
            ? 'Zu viele Anfragen – bitte kurz warten.'
            : `Löschen fehlgeschlagen (${res.status}).`,
          'error'
        );
        return;
      }
    } catch {
      if (btn) btn.disabled = false;
      this.showMessage('Löschen fehlgeschlagen.', 'error');
      return;
    }

    data.generated = data.generated.filter((e) => e.sku !== sku);
    localStorage.setItem(UPLOAD_DATA_KEY, JSON.stringify(data));

    window.dispatchEvent(new CustomEvent('kunstpfote:generated-removed', { detail: { sku } }));
    this.render();
  }

  // ─── Upload ───────────────────────────────────────────────────────────────

  handleFiles(fileList) {
    const data = this.readData() || { originals: [] };
    const remaining = MAX_FILES - data.originals.length;
    if (remaining <= 0) return;

    const errors = [];
    const valid = [];
    for (const file of fileList) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`„${file.name}": ungültiges Format.`);
        continue;
      }
      if (file.size > MAX_SIZE) {
        errors.push(`„${file.name}": zu groß (max. 10 MB).`);
        continue;
      }
      valid.push(file);
    }
    const toAdd = valid.slice(0, remaining);
    if (valid.length > remaining) {
      errors.push(`Nur noch ${remaining} Foto(s) möglich – ${valid.length - remaining} übersprungen.`);
    }
    errors.forEach((msg) => this.showMessage(msg, 'error'));
    if (toAdd.length === 0) return;

    this._uploadQueue = this._uploadQueue.then(() => this.uploadFiles(toAdd));
  }

  async uploadFiles(files) {
    const endpoint = this.getEndpoint();
    if (!endpoint) {
      this.showMessage('Upload-Adresse nicht konfiguriert.', 'error');
      return;
    }

    const label = this.querySelector('.photo-mgmt__upload-label');
    if (label) {
      label.classList.add('photo-mgmt__upload-label--busy');
      label.setAttribute('aria-busy', 'true');
    }

    const formData = new FormData();
    for (const file of files) formData.append('photos', file);
    const sessionId = localStorage.getItem(SESSION_KEY);
    if (sessionId) formData.append('sessionId', sessionId);

    try {
      const res = await fetch(endpoint, { method: 'POST', body: formData });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Serverfehler (${res.status})`);
      }
      const result = await res.json();

      localStorage.setItem(SESSION_KEY, result.sessionId);
      const base = endpoint.replace(/\/upload$/, '');
      const current = this.readData() || { originals: [], generated: [] };
      this.writeData({
        sessionId: result.sessionId,
        originals: [...current.originals, ...result.files],
        uploadBase: base,
      });

      window.dispatchEvent(new CustomEvent('kunstpfote:photos-uploaded', {
        detail: { sessionId: result.sessionId, files: result.files, uploadBase: base },
      }));

      this.render();
      this.showMessage(`${result.files.length} Foto(s) erfolgreich hochgeladen!`, 'success');
    } catch (err) {
      if (label) {
        label.classList.remove('photo-mgmt__upload-label--busy');
        label.removeAttribute('aria-busy');
      }
      this.showMessage(err.message || 'Upload fehlgeschlagen.', 'error');
    }
  }

  showMessage(msg, type) {
    const el = this.querySelector('.photo-mgmt__messages');
    if (!el) return;
    const p = document.createElement('p');
    p.className = `photo-mgmt__msg photo-mgmt__msg--${type}`;
    p.textContent = msg;
    el.appendChild(p);
    if (type === 'success') setTimeout(() => p.remove(), 4000);
  }
}

customElements.define('photo-management-panel', PhotoManagementPanel);

// ─── Nav badge (custom element) ──────────────────────────────────────────────
//
// The theme uses a DOM morph algorithm for soft page transitions. Without
// data-skip-node-update + data-skip-subtree-update, each navigation morphs the
// badge in-place (no disconnect/reconnect), re-adding `hidden` from the
// server-rendered HTML. connectedCallback would never fire to correct it.
//
// Two layers of protection:
//   1. data-skip-* attributes tell morph.js to leave the element untouched.
//   2. attributeChangedCallback catches any residual `hidden` writes (e.g. on
//      the first navigation before the element is fully upgraded).

class PhotoNavBadge extends HTMLElement {
  static get observedAttributes() { return ['hidden']; }

  connectedCallback() {
    this._onUpdate  = () => this._update();
    this._onStorage = (e) => { if (e.key === UPLOAD_DATA_KEY) this._update(); };

    window.addEventListener('kunstpfote:generation-complete', this._onUpdate);
    window.addEventListener('kunstpfote:generated-removed',   this._onUpdate);
    window.addEventListener('storage',                        this._onStorage);

    this._update();
  }

  disconnectedCallback() {
    window.removeEventListener('kunstpfote:generation-complete', this._onUpdate);
    window.removeEventListener('kunstpfote:generated-removed',   this._onUpdate);
    window.removeEventListener('storage',                        this._onStorage);
  }

  attributeChangedCallback(name, _old, newValue) {
    // morph re-added `hidden` — override it if we have generated images
    if (name === 'hidden' && newValue !== null) this._update();
  }

  _update() {
    try {
      const raw = localStorage.getItem(UPLOAD_DATA_KEY);
      const d   = raw ? JSON.parse(raw) : null;
      const count = Array.isArray(d?.generated) ? d.generated.length : 0;
      this.textContent = count > 0 ? String(count) : '';
      this.hidden = count === 0;
    } catch {
      this.hidden = true;
    }
  }
}

customElements.define('photo-nav-badge', PhotoNavBadge);
